import {SearchBar} from '@/components/search/search-bar';
import { Badge } from '@/components/ui/badge';
import { type HeroBanner, type Site } from '@/types/search';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import CarouselImport from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

// react-multi-carousel ships CommonJS that double-wraps its export, so under Vite's
// interop the default import can resolve to `{ __esModule, default: Component }`
// instead of the component itself. Unwrap defensively.
const Carousel = ((CarouselImport as { default?: typeof CarouselImport }).default ?? CarouselImport) as typeof CarouselImport;

interface HeroSearchSectionProps {
  sites?: Site[];
  banners?: HeroBanner[];
}

const ARROW_BASE =
  'absolute top-1/2 z-20 hidden -translate-y-1/2 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/30 md:block';

const CustomLeftArrow = ({ onClick }: { onClick?: () => void }) => (
  <button type="button" onClick={onClick} className={`${ARROW_BASE} left-4`} aria-label="Banner trước">
    <ChevronLeft className="size-5" />
  </button>
);

const CustomRightArrow = ({ onClick }: { onClick?: () => void }) => (
  <button type="button" onClick={onClick} className={`${ARROW_BASE} right-4`} aria-label="Banner tiếp theo">
    <ChevronRight className="size-5" />
  </button>
);

const responsive = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1, slidesToSlide: 1 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 1, slidesToSlide: 1 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1, slidesToSlide: 1 },
};

/**
 * Compact, search-first hero.
 *
 * Layout contract with `web-header.tsx` — the header is `sticky top-0` with an `h-20`
 * inner row, so it consumes 5rem of document flow:
 *   1. `-mt-20` here pulls the hero back under it. Remove it and a 5rem white gap appears.
 *   2. The media layer must stay `absolute inset-0` so it paints *beneath* the header —
 *      that is what makes the header read as transparent on the home page.
 *   3. The content layer carries its own `pt-24`/`md:pt-28` (>= 5rem) to clear the header.
 *   4. The no-banner fallback must stay dark; the transparent header renders white text.
 *
 * Per-slide copy lives inside the carousel so it rotates with its own banner, and is
 * bounded top and bottom so it can never collide with the header or the search card.
 */
export function HeroSearchSection({ sites, banners }: HeroSearchSectionProps) {
  const activeBanners = banners ?? [];
  const hasBanners = activeBanners.length > 0;

  return (
    <section className="relative -mt-20 w-full overflow-hidden" aria-label="Tìm kiếm vé">
      {/* Media layer — paints under the sticky header. */}
      <div className="absolute inset-0">
        {hasBanners ? (
          <Carousel
            responsive={responsive}
            infinite
            autoPlay={activeBanners.length > 1}
            autoPlaySpeed={7000}
            transitionDuration={700}
            pauseOnHover
            showDots={false}
            customLeftArrow={<CustomLeftArrow />}
            customRightArrow={<CustomRightArrow />}
            containerClass="h-full"
            itemClass="h-full"
            sliderClass="h-full"
            arrows={activeBanners.length > 1}
            removeArrowOnDeviceType={[]}
            shouldResetAutoplay={false}
            keyBoardControl
            minimumTouchDrag={80}
            swipeable
            draggable
          >
            {activeBanners.map((banner) => (
              <HeroSlide key={banner.id} banner={banner} />
            ))}
          </Carousel>
        ) : (
          <div className="h-full w-full bg-gray-900" />
        )}
      </div>

      {/* Content layer — the search field is the hero's single primary action. */}
      <div className="relative z-10 container mx-auto flex min-h-100 flex-col justify-end px-4 pt-24 pb-8 md:min-h-115 md:pt-28 md:pb-14">
        <SearchBar sites={sites ?? []} mobileTrigger="inline" />
      </div>
    </section>
  );
}

function HeroSlide({ banner }: { banner: HeroBanner }) {
  const hasCopy = Boolean(banner.title || banner.subtitle || banner.badge);

  return (
    <div
      className="relative h-full bg-cover bg-center bg-no-repeat"
      style={banner.backgroundImage ? { backgroundImage: `url(${banner.backgroundImage})` } : { backgroundColor: '#111827' }}
    >
      {/* Directional scrim — keeps banner detail alive up top while the search card
          below stays legible. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60" />

      {hasCopy && (
        // Bounded band: clears the 5rem header above, clears the search card below.
        <div className="absolute inset-x-0 top-20 bottom-[112px] flex items-center md:bottom-[160px]">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center text-white">
              {banner.badge && (
                <Badge variant="secondary" className="mb-3 border-white/20 bg-white/10 text-white">
                  <Sparkles className="size-3.5" />
                  {banner.badge.text}
                </Badge>
              )}
              {banner.title && <h1 className="line-clamp-2 text-2xl leading-tight font-bold drop-shadow-sm md:text-4xl">{banner.title}</h1>}
              {banner.subtitle && <p className="mx-auto mt-2 line-clamp-2 max-w-2xl text-sm text-white/90 md:text-lg">{banner.subtitle}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
